// O bot lê o placar. Toda decisão de pedir, aceitar, aumentar ou correr passa por ajustePeloPlacar, que olha os pontos
// dos dois trios e o valor em jogo (só o que está na visão do lugar: o placar e o pedido são públicos).
// 1. Correr dá a partida ao adversário: o bot nunca corre.
// 2. Aceitar e perder dá a partida ao adversário, mas correr não: exige o trio mais forte.
// 3. Ganhar a mão dá a partida ao trio: aceita e pede com mais facilidade.
// 4. Trio atrás por 6 ou mais: mais agressivo. Na frente por 6 ou mais: mais conservador. Pesa mais que as regras 2 e 3.
// 5. Mão de onze: jogar ou correr pela chance de ganhar a partida em cada placar (chanceDaPartida), que pesa os pontos
//    do adversário: correr dá 1 a ele; jogar e perder dá 3.
import { nextLevel } from './cards.ts';
import { ONZE_POINTS, WIN_POINTS } from './house-rules.ts';
import type { Team } from './types.ts';
import type { PlayerView } from './view.ts';

// Quanto cada regra muda a exigência, no saldo da chance da mão (ganha menos perde).
export const PESO_DO_PLACAR = { perder: 0.1, ganhar: 0.1, distancia: 0.2 } as const;
export const DISTANCIA = 6;

export interface AjusteDoPlacar {
  nivel: number; // valor em jogo: o pedido aberto, o próximo valor (para pedir) ou 3 na mão de onze
  vale: number; // o que o adversário ganha se o trio correr (ou deixar de pedir e perder)
  naoCorre: boolean; // regra 1
  perder: boolean; // regra 2: aceitar e perder dá a partida ao adversário, e correr não
  ganhar: boolean; // regra 3: ganhar a mão dá a partida ao trio
  distancia: number; // pontos do trio menos os do adversário
  margem: number; // exigência a mais (positiva) ou a menos (negativa) no saldo da chance, pelas regras 2 a 4
}

export function ajustePeloPlacar(v: PlayerView, trio: Team): AjusteDoPlacar {
  const h = v.hand, nos = v.scores[trio], eles = v.scores[1 - trio]!;
  let nivel: number, vale: number;
  if (h?.special === 'onze') { nivel = 3; vale = 1; }
  else if (h?.pending && h.pending.byTeam !== trio) { nivel = h.pending.level; vale = h.value; }
  else { vale = h?.value ?? 1; nivel = nextLevel(h?.value ?? 1) ?? vale; }
  const naoCorre = eles + vale >= WIN_POINTS;
  const perder = eles + nivel >= WIN_POINTS && !naoCorre, ganhar = nos + nivel >= WIN_POINTS;
  const distancia = nos - eles;
  let margem = 0;
  if (perder) margem += PESO_DO_PLACAR.perder;
  if (ganhar) margem -= PESO_DO_PLACAR.ganhar;
  if (distancia <= -DISTANCIA) margem -= PESO_DO_PLACAR.distancia;
  if (distancia >= DISTANCIA) margem += PESO_DO_PLACAR.distancia;
  return { nivel, vale, naoCorre, perder, ganhar, distancia, margem };
}

// Pontos de uma mão normal, medidos em partidas de bots (test/calibracao.test.ts): a base da chance de ganhar a
// partida em cada placar.
export const VALOR_DAS_MAOS: Readonly<Record<number, number>> = { 1: 0.665, 3: 0.104, 6: 0.105, 9: 0.065, 12: 0.061 };

// Chance de ganhar a partida com `nos` x `eles`, os dois trios iguais. Mão normal: cada trio leva a metade, com os
// pontos de VALOR_DAS_MAOS. Mão de onze: o trio com 11 joga quando a chance da mão (q, de 0 a 1, sem preferência)
// compensa: q + (1 - q) x chance com o adversário 3 pontos à frente, contra a chance com ele 1 ponto à frente.
// Mão de ferro: metade.
const TABELA: number[][] = (() => {
  const W: number[][] = Array.from({ length: WIN_POINTS }, () => new Array<number>(WIN_POINTS).fill(0));
  const w = (a: number, b: number): number => (a >= WIN_POINTS ? 1 : b >= WIN_POINTS ? 0 : W[a]![b]!);
  W[ONZE_POINTS]![ONZE_POINTS] = 0.5;
  for (let b = ONZE_POINTS - 1; b >= 0; b--) {
    const w3 = w(ONZE_POINTS, b + 3), w1 = w(ONZE_POINTS, b + 1);
    const t = Math.min(1, Math.max(0, (w1 - w3) / (1 - w3))); // joga com q a partir de t
    W[ONZE_POINTS]![b] = (1 - t * t) / 2 + w3 * ((1 - t) - (1 - t * t) / 2) + t * w1;
  }
  for (let a = 0; a < ONZE_POINTS; a++) W[a]![ONZE_POINTS] = 1 - W[ONZE_POINTS]![a]!;
  for (let s = 2 * (ONZE_POINTS - 1); s >= 0; s--) {
    for (let a = Math.min(ONZE_POINTS - 1, s); a >= 0 && s - a < ONZE_POINTS; a--) {
      const b = s - a;
      let x = 0;
      for (const [v, p] of Object.entries(VALOR_DAS_MAOS)) x += p * (0.5 * w(a + Number(v), b) + 0.5 * w(a, b + Number(v)));
      W[a]![b] = x;
    }
  }
  return W;
})();

export function chanceDaPartida(nos: number, eles: number): number {
  return nos >= WIN_POINTS ? 1 : eles >= WIN_POINTS ? 0 : TABELA[nos]![eles]!;
}
